import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Typography, Card } from "@material-tailwind/react";

import Loader from "../../../../components/Loader";
import { RootState } from "../../../../redux";
import { Vehicle, updateVehicleRequest, Store } from "../../../../redux/vehicles/actions";
import { Field, renderField, getField } from "../../../../components/DetailsFormField";

const DETALIS_FIELDS: Field[] = [
    { n: "VIN", f: "vin", type: "text", editable: true },
    { n: "Make", f: "make", type: "text", editable: true },
    { n: "Model", f: "model", type: "text", editable: true },
    { n: "Year", f: "year", type: "number", editable: true },
    { n: "Trim", f: "trim", type: "text", editable: true },
    { n: "Body Style", f: "body_style", type: "select", options: [], editable: true },
    { n: "Drive Type", f: "drive_type", type: "select", options: [], editable: true },
    { n: "Title Type", f: "title_code", type: "select", options: [], editable: true },
    { n: "Date of Purchase", f: "purchase_date", type: "date", editable: true },
    { n: "Purchase Price", f: "purchase_price", type: "money", editable: true },
    { n: "Payment Method", f: "payment_method", type: "select", options: [
        { n: "Cash", f: "payment_method", v: "cash", type: "string" },
        { n: "Floorplan", f: "payment_method", v: "floorplan", type: "string" },
        { n: "Other", f: "payment_method", v: "other", type: "string" }
    ], editable: true },
    { n: "Purchase Location", f: "market", type: "text", editable: true },
    { n: "Assigned Repair Location", f: "store_id", type: "select", options: [], editable: true },
    { n: "Parts Budget", f: "parts_budget", type: "money", editable: true },
    { n: "Labor Budget", f: "labor_budget", type: "money", editable: true },
    { n: "Est. Labor Cost", f: "est_labor_cost", type: "money", editable: false },
    { n: "Total Labor Cost", f: "labor_cost", type: "money", editable: false },
    { n: "Floorplan Cost", f: "floorplan_cost", type: "money", editable: false },
    { n: "Total Parts Cost", f: "parts_cost", type: "money", editable: false },
    { n: "Total Flip Cost", f: "total_expenses", type: "money", editable: false },
    { n: "Profit", f: "profit", type: "money", editable: false, show: 'sale_price' },
    { n: "Est. Profit", f: "estimated_profit", type: "money", editable: false, hide: 'sale_price' },
    { n: "Rent Cost", f: "rent_price", type: "money", editable: false },
];

const SIDE_FIELDS: Field[] = [
    { n: "Sold Price", f: "sale_price", cardClass: "w-full", textClass: "text-success text-xl", type: "money", editable: true, },
    { n: "Engine", f: "engine_type", cardClass: "w-[calc(50%-0.625rem)] mr-2.5 mt-5 inline-block", textClass: "", type: "text", editable: true, },
    { n: "Miles", f: "miles", cardClass: "w-[calc(50%-0.625rem)] mt-5 ml-2.5 inline-block", textClass: "", type: "number", editable: true, },
    { n: "Estimated Sale Price", f: "estimated_sale_price", cardClass: "w-full my-5", textClass: "", type: "money", editable: true, },
    {
        n: "Sale Commission",
        f: "sale_commission.commission",
        cardClass: "w-full my-5",
        textClass: "",
        type: "combined_input",
        type_field: "sale_commission.type",
        input_type: "number",
        options: [
            { n: "Commission", f: "sale_commission.type", v: "%", type: "percent" },
            { n: "Fixed", f: "sale_commission.type", v: "$", type: "money" },
        ],
        editable: true,
    },
];

export function DashboardVehicleInventoryTabDetails() {
    const dispatch = useDispatch();
    const { vehicle, stores } = useSelector((state: RootState) => state.vehicles);
    const { constants } = useSelector((state: RootState) => state.auth);
    const [isEdit, setIsEdit] = useState(false);
    const [newVehicle, setNewVehicle] = useState<Vehicle>({} as Vehicle);

    useEffect(() => {
        if (constants) {
            DETALIS_FIELDS.forEach((field) => {
                if (field.f === "title_code" && field.options?.length === 0) {
                    field.options = constants.TITLE_TYPES.map((type: any) => ({
                        n: type[1],
                        f: field.f,
                        v: type[0],
                        type: "string",
                    }));
                } else if (field.f === "body_style" && field.options?.length === 0) {
                    field.options = constants.VEHICLE_BODY_STYLES.map((type: any) => ({
                        n: type[1],
                        f: field.f,
                        v: type[0],
                        type: "string",
                    }));
                } else if (field.f === "drive_type" && field.options?.length === 0) {
                    field.options = constants.VEHICLE_DRIVE_TYPES.map((type: any) => ({
                        n: type[1],
                        f: field.f,
                        v: type[0],
                        type: "string",
                    }));
                }
            });
        }
    }, [constants]);

    useEffect(() => {
        if (stores) {
            DETALIS_FIELDS.forEach((field) => {
                if (field.f === "store_id" && field.options?.length === 0) {
                    field.options = stores.map((store: Store) => ({
                        n: `${store.title} ${store.location}`,
                        f: field.f,
                        v: store.id.toString(),
                        type: "string",
                    }));
                }
            });
        }
    }, [stores]);

    useEffect(() => {
        if (vehicle) {
            setNewVehicle(vehicle);
        }
    }, [vehicle]);

    const saveEdit = () => {
        setIsEdit(false);

        const update_fields = Object.keys(newVehicle).filter(
            (key) => newVehicle[key as keyof Vehicle] !== vehicle?.[key as keyof Vehicle]
        );

        if (update_fields.length === 0) return;

        dispatch(
            updateVehicleRequest({
                vehicle: newVehicle,
                update_fields: [
                    'id',
                    ...update_fields,
                ]
            })
        );
    };

    return !vehicle ? (
        <Loader />
    ) : (
        <React.Fragment>
            <div className="flex flex-col md:flex-row gap-5">
                <Card className="p-2 md:p-5 gap-y-5 flex flex-col w-full md:w-3/4">
                    <Card.Header>
                        <div className="flex justify-between">
                            <div className="">
                                <Typography className="block font-bold text-primary">
                                    Basic Information
                                </Typography>
                                <Typography type="small" className="block text-foreground">
                                    Update your profile information below.
                                </Typography>
                            </div>
                            {isEdit ? (
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        color="error"
                                        className="h-8 flex items-center gap-2"
                                        onClick={() => setIsEdit(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outline"
                                        color="success"
                                        className="h-8 flex items-center gap-2"
                                        onClick={saveEdit}
                                    >
                                        Save
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    color="secondary"
                                    className="h-8 flex items-center gap-2"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setNewVehicle(vehicle);
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body className="!p-0 m-2">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 justify-stretch">
                            {DETALIS_FIELDS.filter((field) => {
                                return (!field?.hide || !getField(field.hide, newVehicle)) && (!field?.show || getField(field.show, newVehicle))
                            }).map((field, idx) => (
                                <div className="" key={idx}>
                                    {renderField(field, isEdit, newVehicle, setNewVehicle)}
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
                <div className="w-full md:w-1/4">
                    {SIDE_FIELDS.map((field, idx) => (
                        <Card className={`w-flll h-max ${field.cardClass || ''}`} key={idx}>
                            <Card.Body>
                                {renderField(field, isEdit, newVehicle, setNewVehicle)}
                            </Card.Body>
                        </Card>
                    ))}

                    <Card className="w-flll h-max">
                        <Card.Body>
                            <div className="relative w-full h-[200px] mt-2 rounded-lg overflow-hidden">
                                <iframe
                                    className="absolute top-0 left-0 w-full h-full border-0"
                                    loading="lazy"
                                    src={`https://www.openstreetmap.org/export/embed.html?bbox=-180,-90,180,90&layer=mapnik&marker=${encodeURIComponent(
                                        vehicle?.store?.location || ''
                                    )}&zoom=16&show_address=1`}
                                    allowFullScreen
                                />
                            </div>
                            <Typography className="mt-2 text-sm text-gray-600">
                                {vehicle?.store?.location || ''}
                            </Typography>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </React.Fragment>
    );
}
